<script setup>
import { ref, watch, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { timeAgo, absoluteTime } from 'dashboard/helper/timeAgo';
import { messageTimestamp } from 'shared/helpers/timeHelper';

// Bloco "Webchat" do cartão de contato (.ai/webchat-aparelhos-plan.md §3): o
// que o atendente precisa saber do lado do cliente antes de decidir como
// responder — em que aparelho ele está, se instalou o app, se os avisos
// funcionam (e por que não), quando foi o último aviso e se tocou nele.
// Sem aparelho, o bloco não aparece. A presença "na tela agora" é a do
// contato (mesma do avatar): re-busca quando ela muda.
//
// Divulgação progressiva em 3 formas, porque o cartão divide a coluna com
// "Agente atribuído"/"Time" — os cards soltos empurravam os dois pra fora da
// tela, e é por eles que o atendente abre o painel:
//   1. recolhido (padrão): UMA linha, a mesma altura das linhas de e-mail e
//      telefone, com o sinal que mais importa (bloqueado > na tela > visto) e
//      se o aviso chega em algum aparelho;
//   2. aberto: uma linha por aparelho, estado em ícones;
//   3. aparelho aberto: o detalhe daquele aparelho, com o motivo por extenso.
// O aberto/fechado da forma 2 é do atendente (ui_settings), não da conversa.
//
// E o bloco sobe de forma SOZINHO quando o estado pede (ver "abertura
// automática" mais abaixo): cliente na tela abre a lista; aparelho bloqueado
// abre o detalhe dele. É transitório — nunca grava no ui_settings — e some
// quando o motivo passa, devolvendo a forma que o atendente escolheu.
const props = defineProps({
  contactId: { type: [Number, String], required: true },
  availabilityStatus: { type: String, default: null },
});
const { t } = useI18n();
const { isContactSidebarItemOpen, toggleSidebarUIState } = useUISettings();

const OPEN_KEY = 'is_webchat_devices_open';

const devices = ref([]);
const failed = ref(false);
const openDeviceId = ref(null);

// Brilho de chegada: só na TRANSIÇÃO offline → na tela de um contato já
// carregado — abrir uma conversa com o cliente já na tela não é "chegada".
const ARRIVAL_MS = 4000;
const arrived = ref(false);
const loadedContactId = ref(null);
let arrivalTimer = null;

const flashArrival = () => {
  arrived.value = true;
  clearTimeout(arrivalTimer);
  arrivalTimer = setTimeout(() => {
    arrived.value = false;
  }, ARRIVAL_MS);
};
onBeforeUnmount(() => clearTimeout(arrivalTimer));

const load = async () => {
  const id = props.contactId;
  const wasOnline =
    loadedContactId.value === id && devices.value.some(d => d.online);
  failed.value = false;
  try {
    const { data } = await ContactAPI.getWebchatDevices(id);
    // Troca de conversa no meio da busca: a resposta é de outro contato.
    if (props.contactId !== id) return;
    devices.value = data.payload || [];
    if (
      loadedContactId.value === id &&
      !wasOnline &&
      devices.value.some(d => d.online)
    ) {
      flashArrival();
    }
    loadedContactId.value = id;
  } catch {
    if (props.contactId !== id) return;
    // Divulgado (não silencioso): o bloco diz que não carregou.
    failed.value = true;
  }
};

watch(
  () => [props.contactId, props.availabilityStatus],
  ([id], [previousId] = []) => {
    if (!id) return;
    if (id !== previousId) {
      devices.value = [];
      openDeviceId.value = null;
      arrived.value = false;
    }
    load();
  },
  { immediate: true }
);

const visible = computed(() => failed.value || devices.value.length > 0);
const toggleDevice = id => {
  openDeviceId.value = openDeviceId.value === id ? null : id;
};

// Quem está na tela primeiro, depois o visto mais recente: a 1ª linha é o
// aparelho em que a resposta tem mais chance de ser lida.
const sortedDevices = computed(() =>
  [...devices.value].sort(
    (a, b) =>
      Number(!!b.online) - Number(!!a.online) ||
      (b.last_seen_at || 0) - (a.last_seen_at || 0)
  )
);

const platformLabel = platform =>
  platform ? t(`CONTACT_PANEL.WEBCHAT_DEVICES.PLATFORM.${platform}`) : '';

// "Computador Windows · Chrome": dois computadores do mesmo cliente (ou o
// link aberto noutra máquina) não podem sair com o mesmo nome.
const deviceTitle = device =>
  [
    [platformLabel(device.platform), device.desktop_os]
      .filter(Boolean)
      .join(' '),
    device.browser,
    device.device_model,
  ]
    .filter(Boolean)
    .join(' · ');

// Na linha da forma 2 o ícone já diz "computador", e os 3 ícones de estado
// disputam a largura: sai o que o ícone repete e o modelo (vai pro detalhe).
const deviceShortTitle = device =>
  [
    device.platform === 'desktop' && device.desktop_os
      ? device.desktop_os
      : platformLabel(device.platform),
    device.browser,
  ]
    .filter(Boolean)
    .join(' · ');

const deviceIcon = device => {
  if (device.platform === 'desktop') return 'i-ph-desktop';
  if (device.platform === 'ios') return 'i-ph-device-mobile';
  return 'i-ph-device-mobile-camera';
};

// Desempata o que o nome não separa (dois Chrome no Windows).
const firstSeenLabel = device =>
  device.created_at
    ? t('CONTACT_PANEL.WEBCHAT_DEVICES.FIRST_SEEN', {
        date: messageTimestamp(device.created_at, 'dd/MM'),
      })
    : '';

const seenLabel = device => {
  if (device.online) return t('CONTACT_PANEL.WEBCHAT_DEVICES.ONLINE');
  if (device.last_seen_at) {
    return t('CONTACT_PANEL.WEBCHAT_DEVICES.SEEN', {
      when: timeAgo(device.last_seen_at),
    });
  }
  return t('CONTACT_PANEL.WEBCHAT_DEVICES.NEVER_SEEN');
};

// Por que o aviso não chega: o motivo importa mais que o estado.
const pushLabel = device => {
  if (device.push_state === 'on')
    return t('CONTACT_PANEL.WEBCHAT_DEVICES.PUSH.on');
  if (device.push_state === 'denied')
    return t('CONTACT_PANEL.WEBCHAT_DEVICES.PUSH.denied');
  if (device.in_app_browser)
    return t('CONTACT_PANEL.WEBCHAT_DEVICES.PUSH.in_app');
  if (device.push_state === 'unsupported' && device.platform === 'ios')
    return t('CONTACT_PANEL.WEBCHAT_DEVICES.PUSH.ios_not_installed');
  if (device.push_state === 'unsupported')
    return t('CONTACT_PANEL.WEBCHAT_DEVICES.PUSH.unsupported');
  return t('CONTACT_PANEL.WEBCHAT_DEVICES.PUSH.off');
};

const lastPushLabel = device => {
  if (!device.last_push_at) return '';
  const when = timeAgo(device.last_push_at);
  const clicked =
    device.last_push_clicked_at &&
    device.last_push_clicked_at >= device.last_push_at;
  return t(
    clicked
      ? 'CONTACT_PANEL.WEBCHAT_DEVICES.LAST_PUSH_CLICKED'
      : 'CONTACT_PANEL.WEBCHAT_DEVICES.LAST_PUSH',
    { when }
  );
};

// ---- forma 1: o resumo de uma linha ----

// Tempo curtíssimo ("4 h", "2 d"): o MESMO no resumo e nas linhas, senão o
// resumo diz "5 horas" e a linha "4 h" do mesmo aparelho.
const seenCompact = device => {
  if (device.online) return t('CONTACT_PANEL.WEBCHAT_DEVICES.NOW');
  if (!device.last_seen_at) return '—';
  const minutes = Math.max(
    1,
    Math.floor((Date.now() / 1000 - device.last_seen_at) / 60)
  );
  if (minutes < 60)
    return t('CONTACT_PANEL.WEBCHAT_DEVICES.AGO.min', { n: minutes });
  if (minutes < 60 * 24)
    return t('CONTACT_PANEL.WEBCHAT_DEVICES.AGO.h', {
      n: Math.floor(minutes / 60),
    });
  return t('CONTACT_PANEL.WEBCHAT_DEVICES.AGO.d', {
    n: Math.floor(minutes / (60 * 24)),
  });
};

const pushOnCount = computed(
  () => devices.value.filter(d => d.push_state === 'on').length
);
const anyBlocked = computed(() => devices.value.some(d => d.blocked));
const anyOnline = computed(() => devices.value.some(d => d.online));

// ---- abertura automática ----
// O motivo que faz o bloco abrir sem clique, na mesma ordem do resumo.
const autoReason = computed(() => {
  if (failed.value) return null;
  if (anyBlocked.value) return 'blocked';
  if (anyOnline.value) return 'online';
  return null;
});

// O atendente fechou enquanto estava aberto sozinho: respeita, mas só pra
// AQUELE motivo — cliente que sai e volta à tela reage de novo (e trocar de
// contato zera os aparelhos, então o motivo cai e a dispensa junto).
const dismissedReason = ref(null);
watch(autoReason, reason => {
  if (reason !== dismissedReason.value) dismissedReason.value = null;
});

const savedOpen = computed(() => isContactSidebarItemOpen(OPEN_KEY));
const autoOpen = computed(
  () => !!autoReason.value && autoReason.value !== dismissedReason.value
);
const isOpen = computed(() => savedOpen.value || autoOpen.value);

const toggleOpen = () => {
  if (failed.value) return;
  if (isOpen.value) {
    if (autoOpen.value) dismissedReason.value = autoReason.value;
    if (savedOpen.value) toggleSidebarUIState(OPEN_KEY);
    return;
  }
  // Reabrir o que o atendente dispensou não mexe na preferência dele.
  if (autoReason.value && dismissedReason.value === autoReason.value) {
    dismissedReason.value = null;
    return;
  }
  toggleSidebarUIState(OPEN_KEY);
};

// Aparelho bloqueado vai direto pra forma 3: o motivo (3 erros na
// confirmação — link encaminhado?) é o que o atendente precisa ler.
const blockedDeviceId = computed(
  () => devices.value.find(d => d.blocked)?.id ?? null
);
watch(blockedDeviceId, id => {
  if (id) openDeviceId.value = id;
});

// Fundo da linha: o aparelho que pede atenção se destaca na lista.
const rowTone = device => {
  if (device.blocked) return 'bg-n-ruby-2';
  if (!device.online) return 'hover:bg-n-alpha-2';
  return arrived.value ? 'bg-n-teal-4' : 'bg-n-teal-2';
};

// Um sinal só, na ordem do que muda a decisão do atendente: aparelho
// bloqueado (link encaminhado?) > cliente com a tela aberta > última vez visto.
const summary = computed(() => {
  if (failed.value) {
    return {
      text: t('CONTACT_PANEL.WEBCHAT_DEVICES.LOAD_FAILED_SHORT'),
      tone: 'text-n-ruby-11',
    };
  }
  if (anyBlocked.value) {
    return {
      text: t('CONTACT_PANEL.WEBCHAT_DEVICES.BLOCKED_SHORT'),
      tone: 'text-n-ruby-11',
    };
  }
  if (anyOnline.value) {
    return {
      text: t('CONTACT_PANEL.WEBCHAT_DEVICES.ONLINE'),
      tone: 'text-n-teal-11',
    };
  }
  const latest = sortedDevices.value[0];
  return {
    text: latest?.last_seen_at
      ? t('CONTACT_PANEL.WEBCHAT_DEVICES.SEEN_SHORT', {
          when: seenCompact(latest),
        })
      : t('CONTACT_PANEL.WEBCHAT_DEVICES.NEVER_SEEN'),
    tone: 'text-n-slate-11',
  };
});

const reachLabel = computed(() =>
  pushOnCount.value
    ? t('CONTACT_PANEL.WEBCHAT_DEVICES.REACH', {
        on: pushOnCount.value,
        total: devices.value.length,
      })
    : t('CONTACT_PANEL.WEBCHAT_DEVICES.NO_REACH')
);

// ---- forma 2: estado da linha em ícones (o texto fica no tooltip) ----

const pushIcon = device =>
  device.push_state === 'on'
    ? 'i-ph-bell-ringing text-n-teal-11'
    : 'i-ph-bell-slash text-n-amber-11';
</script>

<template>
  <section v-if="visible" class="w-full ltr:-ml-1 rtl:-mr-1">
    <button
      type="button"
      class="flex h-5 w-full items-center gap-2 !rounded !p-0 text-start text-sm text-n-slate-11 hover:text-n-slate-12"
      :class="{ '!cursor-default': failed }"
      :aria-expanded="isOpen"
      @click="toggleOpen"
    >
      <span
        class="i-ph-browsers size-3.5 shrink-0 ltr:ml-1 rtl:mr-1"
        aria-hidden="true"
      />
      <span class="shrink-0">
        {{ t('CONTACT_PANEL.WEBCHAT_DEVICES.TITLE') }}
      </span>
      <span
        v-tooltip.top="
          failed ? t('CONTACT_PANEL.WEBCHAT_DEVICES.LOAD_FAILED') : null
        "
        class="flex min-w-0 items-center gap-1"
        :class="summary.tone"
        aria-live="polite"
      >
        <span
          v-if="anyOnline && !anyBlocked && !failed"
          class="relative flex size-1.5 shrink-0"
          aria-hidden="true"
        >
          <span
            v-if="arrived"
            class="absolute inline-flex size-full rounded-full bg-n-teal-9 opacity-75 motion-safe:animate-ping"
          />
          <span
            class="relative inline-flex size-1.5 rounded-full bg-n-teal-9"
          />
        </span>
        <span class="truncate">{{ summary.text }}</span>
      </span>
      <span v-if="!failed" class="ms-auto flex shrink-0 items-center gap-1.5">
        <span
          v-tooltip.left="reachLabel"
          :aria-label="reachLabel"
          class="size-3.5"
          :class="
            pushOnCount
              ? 'i-ph-bell-ringing text-n-teal-11'
              : 'i-ph-bell-slash text-n-amber-11'
          "
        />
        <span class="text-xs tabular-nums text-n-slate-10">
          {{ devices.length }}
        </span>
        <span
          class="i-lucide-chevron-down size-3.5 text-n-slate-10 transition-transform"
          :class="{ 'rotate-180': isOpen }"
          aria-hidden="true"
        />
      </span>
    </button>

    <ul
      v-if="isOpen && !failed"
      class="reset-base mt-1.5 flex list-none flex-col border-n-weak ltr:ml-2.5 ltr:border-l ltr:pl-2 rtl:mr-2.5 rtl:border-r rtl:pr-2"
    >
      <li v-for="device in sortedDevices" :key="device.id">
        <button
          type="button"
          class="flex h-7 w-full items-center gap-1.5 !rounded-md !px-1 !py-0 text-start text-sm text-n-slate-12 motion-safe:transition-colors motion-safe:duration-1000"
          :class="rowTone(device)"
          :aria-expanded="openDeviceId === device.id"
          @click="toggleDevice(device.id)"
        >
          <span
            :class="deviceIcon(device)"
            class="size-3.5 shrink-0 text-n-slate-11"
            aria-hidden="true"
          />
          <span class="min-w-0 flex-1 truncate">
            {{ deviceShortTitle(device) }}
          </span>
          <span
            v-if="device.blocked"
            v-tooltip.left="t('CONTACT_PANEL.WEBCHAT_DEVICES.BLOCKED')"
            class="i-ph-lock-simple size-3.5 shrink-0 text-n-ruby-11"
          />
          <span
            v-else-if="device.verified"
            v-tooltip.left="t('CONTACT_PANEL.WEBCHAT_DEVICES.VERIFIED')"
            class="i-ph-shield-check size-3.5 shrink-0 text-n-teal-11"
          />
          <span
            v-if="device.installed"
            v-tooltip.left="t('CONTACT_PANEL.WEBCHAT_DEVICES.INSTALLED')"
            class="i-ph-download-simple size-3.5 shrink-0 text-n-blue-11"
          />
          <span
            v-tooltip.left="pushLabel(device)"
            :class="pushIcon(device)"
            class="size-3.5 shrink-0"
          />
          <span
            v-tooltip.left="
              device.last_seen_at && !device.online
                ? absoluteTime(device.last_seen_at)
                : seenLabel(device)
            "
            class="w-9 shrink-0 text-end text-xs tabular-nums"
            :class="device.online ? 'text-n-teal-11' : 'text-n-slate-10'"
          >
            {{ seenCompact(device) }}
          </span>
        </button>

        <ul
          v-if="openDeviceId === device.id"
          class="reset-base mb-1 ms-6 mt-0.5 flex list-none flex-col gap-0.5 text-xs text-n-slate-11"
        >
          <li class="text-n-slate-12">{{ deviceTitle(device) }}</li>
          <li :class="{ 'text-n-teal-11': device.online }">
            {{ seenLabel(device) }}
          </li>
          <li
            :class="
              device.push_state === 'on' ? 'text-n-teal-11' : 'text-n-amber-11'
            "
          >
            {{ pushLabel(device) }}
          </li>
          <li v-if="device.blocked" class="text-n-ruby-11">
            {{ t('CONTACT_PANEL.WEBCHAT_DEVICES.BLOCKED') }}
          </li>
          <li v-else-if="device.verified">
            {{ t('CONTACT_PANEL.WEBCHAT_DEVICES.VERIFIED') }}
          </li>
          <li v-if="device.installed">
            {{ t('CONTACT_PANEL.WEBCHAT_DEVICES.INSTALLED') }}
          </li>
          <li v-if="lastPushLabel(device)">{{ lastPushLabel(device) }}</li>
          <li v-if="firstSeenLabel(device)" class="text-n-slate-10">
            {{ firstSeenLabel(device) }}
          </li>
        </ul>
      </li>
    </ul>
  </section>
</template>
